import { Unsubscribe } from '@superbet-group/web.lib.feature-toggle-service';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import User from '@core/models/user/User';
import store from '@store/index';
import {
    Ref,
    UnwrapRef,
    ref,
    unref,
    computed,
    onMounted,
    onBeforeUnmount,
} from '@vue/composition-api';

/**
 * A callback to handle the flag value being ready.
 *
 * @param value The flag value.
 */
type OnFlagValueReadyCallback<T> = (value: UnwrapRef<T>) => void | Promise<void>;

/**
 * A hook to handle the flag value being ready.
 *
 * This should be called during the setup of the component. It will add a callback to be triggered when the flag is ready.
 *
 * The callback is triggered the first time the flag is read from Launch Darkly, and every time the flag is updated.
 *
 * If Launch Darkly is unreachable, the initial value will be used.
 *
 * @param callback The callback to be triggered when the flag is ready.
 */
type OnFlagValueReadyHook<T> = (callback: OnFlagValueReadyCallback<T>) => void;

/**
 * An array with the ref to the flag value and a hook to handle the flag being ready.
 */
type GetFeatureFlagReturnValue<T> = [ Ref<UnwrapRef<T>>, OnFlagValueReadyHook<T> ];

/**
 * The timeout for the context to be updated before reading the flag.
 *
 * This is just a safeguard, it should not be necessary.
 */
const fallbackTimeout = 3000;

/**
 * Reads a feature flag, making sure that the Launch Darkly context is correctly set.
 *
 * This will use getFeatureValue, not subscribeToFeatureChanges, meaning it WILL NOT listen to flag changes in real time.
 *
 * It will, however, respond to context changes, meaning the flag will be updated when the user logs in or out.
 *
 * @param name The name of the flag.
 * @param defaultValue The initial value of the flag, and also the fallback value when the flag is not found or coult not be read.
 * @returns An array with the ref to the flag value and a hook to handle the flag being ready.
 */
export function useGetFeatureFlag<T extends string | boolean>(name: string, defaultValue: T): GetFeatureFlagReturnValue<T> {
    // sometimes the Launch Darkly context is not set up, but the user is.
    // when it is not the case we can just read the flag value and get on with it.
    // however, when it is the case, we would get the wrong flag value if we tried to read it.
    // we would either:
    // - get the flag for an anonymous user, when in truth there is a logged one.
    // - get the flag for some user, when in truth the user has just logged off.
    // in either of these cases, we have to wait for context is updated before reading the flag.
    // the context should be updated in a few milliseconds, but we will wait for 3 seconds just to be sure.
    // if the context is not updated, it is most probably due to a bug elsewhere. in this case, we will read the flag anyway.

    const flag = ref<T>(defaultValue);
    const user = computed(() => store.getters['data/authentication/user'] as User | null);
    const callbacks: OnFlagValueReadyCallback<T>[] = [];

    let unsubscribeContextChange: Unsubscribe | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let ready = false;

    onMounted(async () => {
        if (
            (user.value !== null && featureFlagService.lastContext.user?.anonymous)
            || (user.value === null && !featureFlagService.lastContext.user?.anonymous)
        ) {
            timeout = setTimeout(() => handleTimeout(), fallbackTimeout);
        } else {
            await updateFlag();
        }

        unsubscribeContextChange = featureFlagService.subscribeToContextChange(() => handleContextChange());
    });

    onBeforeUnmount(() => {
        // if the timeout is still running, we don't need it anymore.
        if (timeout !== undefined) {
            clearTimeout(timeout);
            timeout = undefined;
        }

        unsubscribeContextChange?.();
    });

    function handleTimeout() {
        // the context has not changed in 3 seconds, we will read the flag value anyway.
        // this should not happen, but it is fallback behavior in case something goes wrong.
        timeout = undefined;
        updateFlag();
    }

    function handleContextChange() {
        // the context has changed already, we don't need the timeout anymore.
        if (timeout !== undefined) {
            clearTimeout(timeout);
            timeout = undefined;
        }

        updateFlag();
    }

    async function updateFlag() {
        let value: T;
        try {
            value = await featureFlagService.getFeatureValue(name, defaultValue);
        } catch (error) {
            value = defaultValue;
        }
        const newValue = unref(value) as UnwrapRef<T>;

        // since LD returns plain values (strings or booleans), we can safely use simple comparison (!==) here.
        if (!ready || flag.value !== newValue) {
            flag.value = newValue;
            ready = true;
            await Promise.all(callbacks.map((callback) => callback(newValue)));
        }
    }

    function addCallback(callback: (value: UnwrapRef<T>) => void | Promise<void>) {
        callbacks.push(callback);
    }

    return [flag, addCallback];
}
